import { FieldMappingsConfig } from '../transformationRules/fieldMappingTypes';
import { FlowDirectionKey } from './flowMapTypes';

// Which route record a field-mapping edit belongs to, and which end of it plays
// the provider. A bidirectional pair is TWO route records with the roles
// swapped, so getting this wrong writes one direction's rule onto the other's
// configuration — hence one pure function, tested, rather than the resolution
// living inline in three handlers (panel display, save, delete).
export type RulesTargetInput = {
  direction: FlowDirectionKey;
  // The destination account the arrow points at (or comes from).
  site: { id: string } | null | undefined;
  // This Route's provider account, and this Route's own record + configuration.
  provider: { id: string } | null | undefined;
  routeRecordId: string | undefined;
  routeConfigName: string;
  routeConfigData: FieldMappingsConfig;
  // The reverse Route of each site: its record id and cached configuration.
  reverseConfigBySite: Record<
    string,
    { routeId: string; name: string; data: FieldMappingsConfig }
  >;
};

export type RulesTarget = {
  // The record the PATCH/DELETE addresses. Undefined = nothing to write to,
  // which the handlers must treat as "cannot save".
  recordId: string | undefined;
  providerId: string;
  counterpartId: string;
  configName: string;
  data: FieldMappingsConfig;
};

export function resolveRulesTarget(
  input: RulesTargetInput,
): RulesTarget | null {
  const { direction, site, provider, reverseConfigBySite } = input;
  if (!site) return null;
  if (direction === 'out') {
    return {
      recordId: input.routeRecordId,
      providerId: provider?.id ?? '',
      counterpartId: site.id,
      configName: input.routeConfigName,
      data: input.routeConfigData,
    };
  }
  const reverse = reverseConfigBySite[site.id];
  return {
    // Roles are swapped on the way back: the site sends, our provider receives.
    recordId: reverse?.routeId,
    providerId: site.id,
    counterpartId: provider?.id ?? '',
    configName: reverse?.name ?? '',
    data: reverse?.data ?? { field_mappings: {} },
  };
}
